import Attachments from '../Attachments/'

import firebase from '@/libs/firebase'

import upload from '@/api/upload'

export default {
  components: {
    Attachments
  },

  data: function () {
    return {
      attachments: [],
      uploadedAttachments: 0,
      uploadQueue: {},
      monitor: {}
    }
  },

  computed: {
    numAttachments: function () {
      return this.attachments.length
    },
    deletedAttachments: function () {
      return this.$refs.attachments ? this.$refs.attachments.deleted : []
    },
    uploadingAttachments: function () {
      return this.uploadedAttachments !== this.numAttachments
    }
  },

  methods: {
    uploadAttachments: function () {
      this.resetUploadQueue()

      this.uploadedAttachments = 0
      return new Promise((resolve, reject) => {
        if (!this.attachments.length) {
          return resolve()
        }

        firebase.projectBoard.signIn().then(() => {
          this.attachments.forEach((attachment) => {
            this.upload(attachment).then(() => {
              this.uploadedAttachments++
              if (this.uploadedAttachments === this.attachments.length) {
                resolve()
              }
            }, error => reject(error)).catch(error => reject(error))
          })
        }, error => reject(error)).catch(error => reject(error))
      })
    },

    upload: function (attachment) {
      return new Promise((resolve, reject) => {
        if (attachment.file || (attachment.url && attachment.url.match(/^data:/))) {
          this.queueUploadAttachment(attachment, resolve, reject)
        } else {
          if ((attachment.type === 'link' || attachment.type === 'iframe') && attachment.metadata.key) {
            attachment.key = attachment.metadata.key
            delete attachment.metadata.key
          }
          resolve()
        }
      })
    },

    resetUploadQueue: function () {
      this.uploadQueue = { started: false, queue: [] }
    },

    queueUploadAttachment: function (attachment, resolve, reject) {
      this.uploadQueue.queue.push({ attachment, resolve, reject })
      if (!this.uploadQueue.started) {
        this.processUploadQueue()
      }
    },

    processUploadQueue: function () {
      if (this.uploadQueue.queue.length > 0) {
        this.uploadQueue.started = true
        const item = this.uploadQueue.queue.shift()
        const resolve = () => {
          item.resolve()
          this.processUploadQueue()
        }
        const reject = (error) => {
          item.reject(error)
        }
        this.uploadAttachment(item.attachment, resolve, reject)
      } else {
        this.uploadQueue.started = false
      }
    },

    uploadAttachment: function (attachment, resolve, reject) {
      if (this.error) {
        return reject(this.error)
      }

      const path = `attachments/${this.currentUser.uuid}`
      const key = attachment.key || new Date().getTime().toString(16).split('').reverse().join('')

      const bucket = firebase.storage().ref(path)

      delete this.monitor
      this.monitor = { task: null, progress: 0, done: false, error: false, uploading: false, key, attachment, bucket }

      if (attachment.file) {
        this.monitor.task = bucket.child(key).put(attachment.file)
      } else {
        this.monitor.task = bucket.child(key).putString(attachment.url, 'data_url')
      }

      this.monitorUpload(resolve, reject)
    },

    monitorUpload: function (resolve, reject) {
      this.monitor.task.on('state_changed', (snapshot) => {
        this.monitor.uploading = snapshot.state === firebase.storage.TaskState.RUNNING
        this.monitor.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      }, (error) => {
        this.monitor.task = null
        this.monitor.error = true
        this.monitor.uploading = false
        reject(error)
      }, () => {
        try {
          this.monitor.attachment.key = this.monitor.key

          if (this.monitor.attachment.type === 'file') {
            const metadata = { contentDisposition: `inline; filename="${this.monitor.attachment.file.name}"` }
            this.monitor.bucket.child(this.monitor.key).updateMetadata(metadata)
          }

          delete this.monitor.attachment.file

          this.monitor.bucket.child(this.monitor.key).getDownloadURL().then((url) => {
            this.monitor.task = null
            this.monitor.done = true
            this.monitor.uploading = false

            this.logUpload({ type: this.monitor.attachment.type, key: this.monitor.key, url: url })
            this.attachments.forEach((attachment, index) => {
              if (attachment.key === this.monitor.key) {
                delete this.attachments[index].url
                this.attachments[index].url = url
              }
            })

            resolve()
          }, (error) => {
            this.monitor.task = null
            this.monitor.error = true
            this.monitor.uploading = false
            reject(error)
          })
        } catch (error) {
          reject(error)
        }
      })
    },

    logUpload: function (attachment) {
      if (attachment.type === 'file') {
        upload.fileV3(attachment.url, attachment.key)
      } else if (attachment.type === 'image') {
        upload.imageV3(attachment.url, attachment.key)
      } else if (attachment.type === 'whiteboard') {
        upload.whiteboardV3(attachment.url, attachment.key)
      }
    },

    saveAttachments: function () {
      return new Promise((resolve, reject) => {
        this.attachments.forEach((attachment, index) => {
          attachment.priority = index
        })
        this.addAttachments().then(() => {
          this.updateAttachments().then(() => {
            this.deleteAttachments().then(() => resolve(), (error) => reject(error))
          }, (error) => reject(error))
        }, (error) => reject(error))
      })
    },

    deleteAttachments: function () {
      return new Promise((resolve, reject) => {
        if (this.deletedAttachments.length === 0) {
          return resolve()
        }

        let count = 0

        this.deletedAttachments.forEach((attachment) => {
          const params = { id: this.entityID, attachment }
          this.$store.dispatch(this.dispatch.deleteAttachments, params).then(() => {
            if (attachment.type === 'whiteboard' && attachment.key) {
              this._callback('deleteBoard', attachment.key)
            }

            count++
            if (count === this.deletedAttachments.length) {
              return resolve()
            }
          }, (error) => {
            reject(error)
          })
        })
      })
    },

    deleteAttachment: function (position) {
      // RV - I don't think this is called anywhere?
      const attachment = this.attachments[position]
      if (attachment) {
        this.attachments.splice(position, 1)
        this.queueDelete(attachment)
        // RV - Why are we deleting here when we're queing for delete and will
        // later delete in deleteAttachments.
        if (!attachment.id && attachment.type === 'whiteboard' && attachment.key) {
          this._callback('deleteBoard', attachment.key)
        }
      }
    },

    queueDelete: function (attachment) {
      if (attachment.id) {
        this.deletedAttachments.push(attachment)
      }
    },

    addAttachments: function () {
      return new Promise((resolve, reject) => {
        if (this.attachments.length === 0) {
          return resolve()
        }

        const newAttachments = this.attachments.filter((attachment) => !attachment.id)
        if (newAttachments.length === 0) {
          return resolve()
        }
        const params = { id: this.entityID, attachments: newAttachments }
        this.$store.dispatch(this.dispatch.addAttachments, params).then(() => {
          resolve()
        }, (error) => {
          reject(error)
        })
      })
    },

    updateAttachments: function () {
      return new Promise((resolve, reject) => {
        if (this.attachments.length === 0) {
          return resolve()
        }

        const updatedAttachments = this.attachments.filter((attachment) => !!attachment.id)
        if (updatedAttachments.length === 0) {
          return resolve()
        }

        let count = 0

        updatedAttachments.forEach((attachment) => {
          const params = { id: this.entityID, attachment }
          this.$store.dispatch(this.dispatch.updateAttachments, params).then(() => {
            count++
            if (count === updatedAttachments.length) {
              return resolve()
            }
          }, (error) => {
            reject(error)
          })
        })
      })
    },
    displayNewAttachments: function (attachments) {
      this.attachments = attachments
    }
  }
}
